import React from 'react';
import {Link} from 'react-router-dom';
import {Header} from '@/components/Header';
import {
	ArrowRight,
	BadgeCheck,
	CalendarCheck2,
	ChevronRight,
	ClipboardList,
	MapPin,
	ShieldCheck,
	Search,
	Sparkles,
	Star,
	TrendingUp,
	Utensils,
	UsersRound,
} from 'lucide-react';
import {
	categoryMeta,
	communityNotes,
	entityTypeLabels,
	featuredListings,
	getCategory,
	newestListings,
	priceRangeLabels,
	ratingLabel,
	spotlightListing,
	staticStats,
} from './yellowPagesStaticData';

const heroBg = '/assets/yellow-page-hero.png';

const contactLabel = listing => {
	if (listing.primaryCta === 'call') return 'Call';
	if (listing.primaryCta === 'website') return 'Website';
	return 'WhatsApp';
};

const ListingMiniCard = ({listing, compact = false}) => {
	const category = getCategory(listing.categories[0]);
	const Icon = category.icon;

	return (
		<article
			className="group h-full overflow-hidden rounded-2xl border border-orange-100/80 bg-white shadow-[0_12px_40px_rgba(124,45,18,0.08)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(124,45,18,0.13)]">
			<div className="h-1.5" style={{background: `linear-gradient(90deg, ${category.color}, ${category.color}66)`}}/>
			<div className="p-4">
				<div className="flex items-start gap-3">
					<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border bg-white"
					     style={{borderColor: `${category.color}30`, color: category.color, backgroundColor: category.bg}}>
						<Icon className="h-7 w-7" strokeWidth={2.1}/>
					</div>
					<div className="min-w-0 flex-1">
						<div className="mb-1 flex flex-wrap items-center gap-1.5">
							<span className="rounded-md px-2 py-0.5 font-custom-bold text-[0.52rem] uppercase"
							      style={{backgroundColor: `${category.color}14`, color: category.color}}>
								{entityTypeLabels[listing.entityType]}
							</span>
							{listing.verification?.isVerified && (
								<span
									className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-custom-bold text-[0.52rem] uppercase text-emerald-700">
									<BadgeCheck className="h-2.5 w-2.5"/> Verified
								</span>
							)}
						</div>
						<h3 className="truncate font-custom-extra-bold text-[0.98rem] leading-tight text-slate-950">
							{listing.entityName}
						</h3>
						<p className="mt-1 line-clamp-2 font-custom-medium text-[0.72rem] leading-snug text-slate-500">
							{listing.tagline}
						</p>
					</div>
				</div>

				{!compact && (
					<div className="mt-4 grid grid-cols-3 gap-2 text-center">
						<div className="rounded-lg bg-slate-50 px-2 py-2">
							<p className="font-custom-extra-bold text-[0.8rem] text-slate-950">{ratingLabel(listing.rating)}</p>
							<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Rating</p>
						</div>
						<div className="rounded-lg bg-slate-50 px-2 py-2">
							<p className="truncate font-custom-extra-bold text-[0.8rem] text-slate-950">
								{priceRangeLabels[listing.priceRange]}
							</p>
							<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Range</p>
						</div>
						<div className="rounded-lg bg-slate-50 px-2 py-2">
							<p className="truncate font-custom-extra-bold text-[0.8rem] text-slate-950">
								{listing.isPhysical ? listing.location : listing.serviceArea}
							</p>
							<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Serves</p>
						</div>
					</div>
				)}

				<div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
					<div className="min-w-0">
						<p className="font-custom-bold text-[0.52rem] uppercase tracking-wide text-slate-400">Listed by</p>
						<p className="truncate font-custom-extra-bold text-[0.78rem] text-slate-900">{listing.listedBy.displayName}</p>
					</div>
					<button
						className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-950 px-3 py-2 font-custom-bold text-[0.68rem] text-white transition group-hover:bg-orange-600">
						{contactLabel(listing)} <ArrowRight className="h-3 w-3"/>
					</button>
				</div>
			</div>
		</article>
	);
};

const SectionHeader = ({eyebrow, title, text, action}) => (
	<div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
		<div className="max-w-2xl">
			{eyebrow && (
				<p className="mb-2 font-custom-bold text-[0.68rem] uppercase tracking-[0.18em] text-orange-600">
					{eyebrow}
				</p>
			)}
			<h2 className="font-custom-extra-bold text-xl leading-tight text-slate-950 sm:text-2xl">{title}</h2>
			{text && <p className="mt-2 font-custom-medium text-sm leading-relaxed text-slate-500">{text}</p>}
		</div>
		{action}
	</div>
);

export const YellowPagesLandingStatic = () => {
	const searchChips = Object.entries(categoryMeta).slice(0, 6);
	const spotlightIcons = [CalendarCheck2, ClipboardList, Utensils];

	return (
		<>
		<Header/>
		<main className="min-h-screen bg-[#fffaf6] font-custom-regular text-slate-900">
			<section className="relative min-h-[82vh] overflow-hidden bg-slate-950 text-white">
				<div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url(${heroBg})`}}/>
				<div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,24,39,0.92),rgba(67,20,7,0.74),rgba(17,24,39,0.28))]"/>
				<div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#fffaf6] to-transparent"/>

				<div
					className="relative mx-auto grid min-h-[82vh] max-w-7xl grid-cols-1 items-center gap-8 px-4 py-24 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
					<div className="max-w-3xl pt-8">
						<div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 backdrop-blur-md">
							<Sparkles className="h-4 w-4 text-amber-300"/>
							<span className="font-custom-bold text-xs text-white/90">JoglekarKul Yellow Pages</span>
						</div>
						<h1 className="font-custom-black text-3xl leading-[1.02] tracking-normal sm:text-5xl lg:text-6xl">
							Find trusted businesses from families you already know.
						</h1>
						<p className="mt-5 max-w-2xl font-custom-medium text-sm leading-7 text-white/78 sm:text-base">
							A premium directory for JoglekarKul members: discover professional services, festive food,
							retail, education, technology, and home experts with community context built in.
						</p>

						<div className="mt-7 max-w-3xl rounded-2xl border border-white/20 bg-white p-2 shadow-[0_28px_90px_rgba(0,0,0,0.28)]">
							<div className="flex flex-col gap-2 sm:flex-row">
								<label className="flex min-h-[3.25rem] flex-1 items-center gap-3 rounded-xl bg-slate-50 px-4 text-slate-900">
									<Search className="h-5 w-5 shrink-0 text-orange-600"/>
									<input className="w-full bg-transparent font-custom-medium text-sm outline-none placeholder:text-slate-400"
									       placeholder="Search caterers, CA, web studio, music classes..."/>
								</label>
								<label className="flex min-h-[3.25rem] items-center gap-3 rounded-xl bg-slate-50 px-4 text-slate-900 sm:w-56">
									<MapPin className="h-5 w-5 shrink-0 text-orange-600"/>
									<input className="w-full bg-transparent font-custom-medium text-sm outline-none placeholder:text-slate-400"
									       placeholder="City / service area"/>
								</label>
								<Link to="/yellow-pages-static/search"
								      className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl bg-orange-600 px-6 font-custom-bold text-sm text-white transition hover:bg-orange-700">
									Search <ArrowRight className="h-4 w-4"/>
								</Link>
							</div>
						</div>

						<div className="mt-4 flex flex-wrap gap-2">
							{searchChips.map(([key, meta]) => {
								const Icon = meta.icon;
								return (
									<Link key={key} to={`/yellow-pages-static/search?categories=${key}`}
									      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 font-custom-bold text-[0.72rem] text-white/90 backdrop-blur transition hover:bg-white/20">
										<Icon className="h-3.5 w-3.5"/> {meta.short}
									</Link>
								);
							})}
						</div>
					</div>

					<div className="hidden lg:block">
						<div className="ml-auto max-w-md rounded-[1.5rem] border border-white/20 bg-white/14 p-4 shadow-[0_30px_100px_rgba(0,0,0,0.3)] backdrop-blur-xl">
							<div className="mb-4 flex items-center justify-between">
								<div>
									<p className="font-custom-bold text-[0.64rem] uppercase tracking-[0.18em] text-orange-200">
										Featured members
									</p>
									<h2 className="font-custom-extra-bold text-lg">Community business board</h2>
								</div>
								<div className="rounded-full bg-white/15 p-2">
									<Star className="h-5 w-5 fill-amber-300 text-amber-300"/>
								</div>
							</div>
							<div className="space-y-3">
								{featuredListings.map(item => <ListingMiniCard key={item._id} listing={item} compact/>)}
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="relative z-10 mx-auto -mt-12 max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="grid gap-3 rounded-2xl border border-orange-100 bg-white p-3 shadow-[0_18px_60px_rgba(124,45,18,0.1)] sm:grid-cols-3">
					{staticStats.map(stat => (
						<div key={stat.label} className="rounded-xl bg-orange-50/60 px-4 py-4">
							<p className="font-custom-black text-2xl text-slate-950">{stat.value}</p>
							<p className="mt-1 font-custom-bold text-sm text-slate-800">{stat.label}</p>
							<p className="mt-1 font-custom-medium text-xs leading-relaxed text-slate-500">{stat.detail}</p>
						</div>
					))}
				</div>
			</section>

			<section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
				<SectionHeader
					eyebrow="Discover"
					title="Browse by what your family needs next"
					text="Explore trusted member-led businesses and services by the kind of help your family is looking for."
				/>
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{Object.entries(categoryMeta).map(([key, meta]) => {
						const Icon = meta.icon;
						return (
							<Link key={key} to={`/yellow-pages-static/search?categories=${key}`}
							      className="group rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
								<div className="flex items-start gap-3">
									<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
									     style={{backgroundColor: meta.bg, color: meta.color}}>
										<Icon className="h-5 w-5"/>
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-center justify-between gap-2">
											<h3 className="font-custom-extra-bold text-sm text-slate-950">{meta.label}</h3>
											<ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-orange-600"/>
										</div>
										<p className="mt-1 font-custom-medium text-xs leading-relaxed text-slate-500">{meta.description}</p>
									</div>
								</div>
							</Link>
						);
					})}
				</div>
			</section>

			<section className="bg-white py-14">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<SectionHeader
						eyebrow="Featured"
						title="Member businesses with strong trust signals"
						text="A polished card system foregrounds category, verification, rating, service area, and the member behind the listing."
						action={
							<Link to="/yellow-pages-static/search?sortBy=rating"
							      className="inline-flex items-center gap-2 rounded-full border border-orange-200 px-4 py-2 font-custom-bold text-xs text-orange-700 hover:bg-orange-50">
								View all <ArrowRight className="h-3.5 w-3.5"/>
							</Link>
						}
					/>
					<div className="grid gap-4 md:grid-cols-3">
						{featuredListings.map(item => <ListingMiniCard key={item._id} listing={item}/>)}
					</div>
				</div>
			</section>

			<section className="mx-auto grid max-w-7xl gap-6 px-4 py-14 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
				<div className="flex flex-col justify-between rounded-[1.25rem] border border-orange-100 bg-[#301508] p-6 text-white shadow-[0_24px_80px_rgba(67,20,7,0.18)]">
					<div>
					<p className="font-custom-bold text-[0.72rem] uppercase tracking-[0.18em] text-amber-300">Community spotlight</p>
					<h2 className="mt-3 font-custom-black text-3xl leading-tight">
						A directory that feels personal before it feels commercial.
					</h2>
					<p className="mt-4 font-custom-medium text-[0.95rem] leading-8 text-white/78">
						Yellow Pages should motivate members to join because it celebrates their work, not just because it
						lists their phone number. Each listing carries owner, native place, trust, and service details together.
					</p>
					</div>
					<div className="mt-6 grid grid-cols-3 gap-3">
						{spotlightListing.highlights.map((item, index) => {
							const TileIcon = spotlightIcons[index] || Sparkles;
							return (
							<div key={item.text}
							     className="flex flex-col items-center justify-center gap-2 rounded-xl bg-white/10 px-3 py-4 text-center text-white/90">
								<TileIcon className="h-5 w-5 text-amber-300"/>
								<span className="font-custom-bold text-[0.78rem] leading-tight">{item.text}</span>
							</div>
						)})}
					</div>
				</div>
				<div className="grid gap-3">
					<ListingMiniCard listing={spotlightListing} compact/>
					<div className="grid gap-3 sm:grid-cols-3">
						<div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
							<p className="font-custom-black text-xl text-slate-950">{spotlightListing.credentials.experience}</p>
							<p className="mt-1 font-custom-bold text-[0.68rem] uppercase text-slate-400">Experience</p>
						</div>
						<div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
							<p className="font-custom-black text-xl text-slate-950">{spotlightListing.credentials.primaryMetric}</p>
							<p className="mt-1 font-custom-bold text-[0.68rem] uppercase text-slate-400">Community work</p>
						</div>
						<div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
							<p className="font-custom-black text-xl text-slate-950">{ratingLabel(spotlightListing.rating)}</p>
							<p className="mt-1 font-custom-bold text-[0.68rem] uppercase text-slate-400">Rating</p>
						</div>
					</div>
				</div>
			</section>

			<section className="bg-orange-50/70 py-14">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<SectionHeader
						eyebrow="New"
						title="Recently added to the directory"
						text="Fresh listings from members who are ready to serve the community."
					/>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{newestListings.map(item => <ListingMiniCard key={item._id} listing={item} compact/>)}
					</div>
				</div>
			</section>

			<section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
				<div className="rounded-[1.5rem] border border-orange-100 bg-white p-4 shadow-[0_18px_60px_rgba(124,45,18,0.08)] sm:p-6">
					<SectionHeader
						eyebrow="Why join"
						title="A stronger reason for members to come onboard"
						text="The page should make a business owner feel seen, while giving families enough context to confidently contact them."
					/>
					<div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
						<div className="grid gap-3">
							{communityNotes.map(note => {
								const Icon = note.icon;
								return (
									<div key={note.title} className="flex gap-3 rounded-2xl border border-slate-100 bg-[#fffaf6] p-4">
										<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
											<Icon className="h-5 w-5"/>
										</div>
										<div>
											<h3 className="font-custom-extra-bold text-sm text-slate-950">{note.title}</h3>
											<p className="mt-1 font-custom-medium text-xs leading-relaxed text-slate-500">{note.text}</p>
										</div>
									</div>
								);
							})}
						</div>
						<div className="grid content-start gap-3">
							<div className="rounded-2xl bg-slate-950 p-5 text-white">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500">
										<TrendingUp className="h-5 w-5"/>
									</div>
									<div>
										<p className="font-custom-black text-2xl">3 steps</p>
										<p className="font-custom-bold text-xs uppercase text-white/50">From listing to discovery</p>
									</div>
								</div>
								<div className="mt-5 grid gap-2">
									{['Create a complete listing', 'Get verified and published', 'Receive calls, WhatsApp leads, and profile visits'].map((step, index) => (
										<div key={step} className="flex items-center gap-3 rounded-xl bg-white/8 px-3 py-3">
											<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[0.7rem] font-custom-black text-slate-950">{index + 1}</span>
											<p className="font-custom-bold text-xs text-white/82">{step}</p>
										</div>
									))}
								</div>
							</div>
							<div className="grid gap-3 sm:grid-cols-2">
								<div className="rounded-2xl bg-orange-50 p-4">
									<UsersRound className="h-5 w-5 text-orange-600"/>
									<p className="mt-3 font-custom-extra-bold text-sm text-slate-950">For families</p>
									<p className="mt-1 font-custom-medium text-xs leading-relaxed text-slate-500">Find known, contextual, member-led help faster.</p>
								</div>
								<div className="rounded-2xl bg-emerald-50 p-4">
									<ShieldCheck className="h-5 w-5 text-emerald-600"/>
									<p className="mt-3 font-custom-extra-bold text-sm text-slate-950">For businesses</p>
									<p className="mt-1 font-custom-medium text-xs leading-relaxed text-slate-500">Build trust before the first message arrives.</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
				<div className="rounded-[1.5rem] bg-slate-950 p-6 text-white sm:p-8 lg:flex lg:items-center lg:justify-between">
					<div className="max-w-2xl">
						<p className="font-custom-bold text-[0.68rem] uppercase tracking-[0.18em] text-orange-300">Start discovering</p>
						<h2 className="mt-2 font-custom-black text-2xl leading-tight sm:text-3xl">Find the right member business faster.</h2>
						<p className="mt-3 font-custom-medium text-sm leading-7 text-white/70">
							Search by category, service area, price range, trust signals, and the kind of offering you need.
						</p>
					</div>
					<Link to="/yellow-pages-static/search"
					      className="mt-5 inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-3 font-custom-bold text-sm text-white transition hover:bg-orange-700 lg:mt-0">
						Open Yellow Pages <ArrowRight className="h-4 w-4"/>
					</Link>
				</div>
			</section>
		</main>
		</>
	);
};

export default YellowPagesLandingStatic;
